import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

class AuthService {
  constructor() {
    // Set up axios interceptor to include token in requests
    axios.interceptors.request.use(
      (config) => {
        const token = this.getToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Set up response interceptor to handle 401 errors
    axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          this.logout();
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  async register(data) {
    const response = await axios.post(`${API_URL}/auth/register`, data);
    return response.data;
  }

  async verifyEmail(email, passcode) {
    const response = await axios.post(`${API_URL}/auth/verify-email`, { email, passcode });
    return response.data;
  }

  async login(data) {
    const response = await axios.post(`${API_URL}/auth/login`, data);
    return response.data;
  }

  async verifyLoginPasscode(email, passcode) {
    const response = await axios.post(`${API_URL}/auth/verify-passcode`, { 
      email, 
      passcode 
    });
    
    if (response.data.access_token) {
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }
    
    return response.data;
  }

  async requestLoginPasscode(email) {
    const response = await axios.post(`${API_URL}/auth/request-passcode`, { email });
    return response.data;
  }

  async requestPasscode(email) {
    const response = await axios.post(`${API_URL}/auth/request-passcode`, { email });
    return response.data;
  }

  async getCurrentUser() {
    const response = await axios.get(`${API_URL}/auth/me`);
    return response.data;
  }

  async checkAuthStatus() {
    try {
      const response = await axios.get(`${API_URL}/auth/check-status`);
      return response.data.authenticated;
    } catch {
      return false;
    }
  }

  logout() {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  }

  getToken() {
    return localStorage.getItem('token');
  }

  getUser() {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  }

  isAuthenticated() {
    return !!this.getToken();
  }

  async getBusinesses() {
    const response = await axios.get(`${API_URL}/auth/businesses`);
    return response.data;
  }

  async createBusiness(businessData) {
    const response = await axios.post(`${API_URL}/auth/businesses`, businessData);
    return response.data;
  }

  async getUserProfile() {
    const response = await axios.get(`${API_URL}/auth/profile`);
    return response.data;
  }

  async updateDisplayName(displayName) {
    const response = await axios.patch(`${API_URL}/auth/profile/display-name`, {
      display_name: displayName
    });
    return response.data;
  }

  async updatePhoneNumber(phoneNumber) {
    const response = await axios.patch(`${API_URL}/auth/profile/phone-number`, {
      phone_number: phoneNumber
    });
    return response.data;
  }
}

export const authService = new AuthService();